import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import { CSSProperties, Fragment, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useGetBenefitVerifiedStatusQuery,
  useGetBenefitWorklistQuery,
} from "store/queries/benefitComparison";
import styled from "styled-components";
import {
  IComparisonChecksRequest,
  ISourceAgreementCell,
  IWorklistFilters,
  IWorklistRow,
  WorklistState,
} from "types/BenefitComparison/BenefitComparison";

import { BenefitComparisonCluster } from "./BenefitComparisonCluster";
import {
  accuracyText,
  AGREEMENT_HEADING,
  AGREEMENT_TOOLTIP,
  agreementPercent,
  agreementRows,
  agreementSampleText,
  agreementText,
  COMPARISON_BENEFIT_LABELS,
  COMPARISON_BENEFITS,
  COVERAGE_INTRO,
  ENGINE_ACCURACY_TOOLTIP,
  otherSourcesText,
  rangeText,
  REGRESSED_CARD_TEXT,
  ACCURACY_BENEFIT_ORDER,
  SCORECARD_BENEFIT_LABELS,
  SCORECARD_BENEFIT_ORDER,
  verdictsText,
  WORKLIST_INTRO,
  WORKLIST_STATE_LABELS,
  WORKLIST_STATE_SEVERITY,
  WORKLIST_STATES,
} from "./benefitComparisonWording";
import { BenefitWorklistTrend } from "./BenefitWorklistTrend";
import { READ_FROM_LABELS, relativeTime } from "./remitAccuracyWording";

const PARAM = {
  account: "account",
  payer: "payer",
  benefit: "benefit",
  state: "state",
  clusterAccount: "clusterAccount",
  clusterPayer: "clusterPayer",
  clusterPlan: "clusterPlan",
  clusterNoPlan: "clusterNoPlan",
  clusterBenefit: "clusterBenefit",
  clusterReadFrom: "clusterReadFrom",
  clusterState: "clusterState",
} as const;

const CLUSTER_PARAMS = [
  PARAM.clusterAccount,
  PARAM.clusterPayer,
  PARAM.clusterPlan,
  PARAM.clusterNoPlan,
  PARAM.clusterBenefit,
  PARAM.clusterReadFrom,
  PARAM.clusterState,
];

/** 🚨 Client-side paging over the ranked list the API already returned. */
const WORKLIST_PAGE_SIZE = 50;

const oneOf = <T extends string>(value: string | null, allowed: T[]) =>
  value !== null && allowed.includes(value as T) ? (value as T) : undefined;

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-color-secondary">{children}</span>;
}

/**
 * 🚨 `data-pr-tooltip` DOES NOTHING WITHOUT A MOUNTED `<Tooltip>` TARGETING IT. Both info icons on
 * this screen carried the attribute and neither showed anything on hover. One `<Tooltip>` for the
 * whole tab, matched by this class — the pattern the rest of the SPA already uses.
 */
const TOOLTIP_CLASS = "benefitWorklistTip";

/** A card in the scorecard row. Before the first nightly run every figure is a dash, with no copy. */
function Card({
  label,
  children,
  tone,
  testId,
  tooltip,
}: {
  label: string;
  children: React.ReactNode;
  tone?: "danger";
  testId: string;
  tooltip?: string;
}) {
  return (
    <div
      className={`border-1 border-round p-3 h-full flex flex-column gap-2 ${
        tone === "danger" ? "border-red-300 bg-red-50" : "surface-border"
      }`}
      data-testid={testId}
    >
      <span className="align-self-center text-xs font-bold uppercase text-color-secondary">
        {label}
        {tooltip && (
          <i
            className={`pi pi-info-circle ml-2 text-xs ${TOOLTIP_CLASS}`}
            data-pr-tooltip={tooltip}
            data-testid={`${testId}.tooltip`}
          />
        )}
      </span>
      {children}
    </div>
  );
}

/** A count's share, rounded — with "<1%" rather than "0%", because 31 of 6,911 is not nothing. */
const sharePercentText = (count: number, total: number) => {
  if (total === 0) return "—";
  const share = (100 * count) / total;
  if (count > 0 && share < 1) return "<1%";
  return `${Math.round(share)}%`;
};

/**
 * The scorecard's five benefit percentages.
 *
 * 🚨 TWO COLUMNS IN *COLUMN* FLOW above 62rem — deductible, out-of-pocket and co-insurance down the
 * left, copay and physician copay down the right, the arrangement the mockups fixed. Below that the
 * card stacks to ONE column and grows taller rather than squeezing two: at a narrow width the
 * labels wrapped onto two lines each and the percentages stopped lining up, which is the only thing
 * the grid is for. Abbreviating them ("Ded", "Phys Copay") was the alternative and reads worse for
 * the few pixels it saves.
 */
const AccuracyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.25rem 1.5rem;

  @media (min-width: 62rem) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: repeat(3, auto);
    grid-auto-flow: column;
  }
`;

/**
 * The bar rows inside one coverage group: label, track, count.
 *
 * 🚨 The track is CAPPED rather than filling the column. A bar stretched across half the
 * screen to show 8% reads as a measurement error; bounded, the three of them read as one comparison.
 */
const COVERAGE_ROWS: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "10.5rem minmax(4rem, 22rem) 6.5rem",
  gap: "0.75rem 1rem",
  alignItems: "center",
};

/** One bar in the coverage strip. 🚨 One denominator for every bar, so they are comparable. */
function CoverageBar({
  label,
  count,
  total,
  accent,
}: {
  label: string;
  count: number;
  total: number;
  accent?: boolean;
}) {
  const share = total === 0 ? 0 : (100 * count) / total;
  const shareText = sharePercentText(count, total);
  return (
    <>
      <span>{label}</span>
      <div
        className="border-round surface-200 overflow-hidden"
        style={{ height: "6px" }}
      >
        <div
          className={accent ? "bg-orange-600" : "bg-blue-700"}
          style={{
            height: "6px",
            width: `${Math.max(share, count > 0 ? 0.5 : 0)}%`,
          }}
        />
      </div>
      <span className="text-right">
        <strong>{count.toLocaleString()}</strong> <Muted>{shareText}</Muted>
      </span>
    </>
  );
}

/**
 * One benefit's agreement card: the five buckets as bars, over the compared-check count.
 *
 * 🚨 THE DENOMINATOR IS THAT BENEFIT'S OWN `compared`, not a shared one, because AI coverage differs
 * per benefit. So the five bars within ONE card sum to 100% and are comparable to each other; two
 * cards' bars are NOT comparable as counts, which is why every card carries its own sample size.
 */
function AgreementCard({ cell }: { cell: ISourceAgreementCell }) {
  return (
    <div
      className="border-1 surface-border border-round p-3 flex flex-column gap-2"
      data-testid={`benefitWorklist.agreement.${cell.benefit}`}
    >
      <div className="flex flex-column">
        <span className="font-bold">
          {SCORECARD_BENEFIT_LABELS[cell.benefit]}
        </span>
        <span className="text-xs text-color-secondary">
          {agreementSampleText(cell.compared)}
        </span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "8.5rem minmax(2rem, 1fr) 3.75rem",
          gap: "0.4rem 0.6rem",
          alignItems: "center",
        }}
      >
        {agreementRows(cell).map(({ key, label, count }) => {
          const share = agreementPercent(count, cell.compared) ?? 0;
          return (
            <Fragment key={key}>
              <span className="text-sm">{label}</span>
              <div
                className="border-round surface-200 overflow-hidden"
                style={{ height: "6px" }}
              >
                <div
                  // 🚨 "None agree" is the one worth spotting, so it is the one that is not blue.
                  className={key === "none" ? "bg-orange-600" : "bg-blue-700"}
                  style={{
                    height: "6px",
                    width: `${Math.max(share, count > 0 ? 0.5 : 0)}%`,
                  }}
                />
              </div>
              <span className="text-sm text-right">
                {cell.compared === 0 ? (
                  <Muted>—%</Muted>
                ) : (
                  agreementText(count, cell.compared)
                )}
              </span>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

const nameBody = (row: IWorklistRow) => (
  <div className="flex flex-column gap-1">
    <span className="font-semibold">
      {row.accountName} · {row.payerName}
    </span>
    <Muted>
      {row.state === "NOT_CONFIGURED"
        ? "all plans"
        : row.planName ?? "No plan name"}
    </Muted>
  </div>
);

const engineSaysBody = (row: IWorklistRow) => {
  if (row.benefit === null) return <Muted>—</Muted>;
  const text = rangeText(row.engineMin, row.engineMax, row.benefit);
  return text || <Muted>no value</Muted>;
};

/** 🚨 The value AND who says it — "other sources" with no source named is what this replaced. */
const otherSourcesBody = (row: IWorklistRow) => {
  const lines = otherSourcesText(row);
  if (lines.length === 0) return <Muted>—</Muted>;
  return (
    <div className="flex flex-column gap-1">
      {lines.map((line) => (
        <span key={line.source} className="white-space-nowrap">
          {line.value} <Muted>{line.source}</Muted>
        </span>
      ))}
    </div>
  );
};

/**
 * 🚨 "Nothing" is the ABSENCE of a read, so it is greyed like "no value" in Engine says rather than
 * set in body text as though it named somewhere the engine actually looked.
 */
const readFromBody = (row: IWorklistRow) => {
  if (!row.readFrom) return <Muted>—</Muted>;
  const label = READ_FROM_LABELS[row.readFrom] ?? row.readFrom;
  return row.readFrom === "NONE" ? (
    <Muted>{label}</Muted>
  ) : (
    <span className="white-space-nowrap">{label}</span>
  );
};

const stateBody = (row: IWorklistRow) => (
  <Tag
    severity={WORKLIST_STATE_SEVERITY[row.state]}
    value={WORKLIST_STATE_LABELS[row.state]}
    className="white-space-nowrap"
    data-testid={`benefitWorklist.state.${row.state}`}
  />
);

/**
 * CLE-2820 — THE ENGINE WORKLIST. One screen: a scorecard that answers "is the engine getting
 * better" in thirty seconds, a coverage strip that says why a number is thin, and one ranked list of
 * clusters that answers "what do I fix next". Internal, SuperAdmin only.
 *
 * It replaced three views behind a `View` SelectButton (Compare / Review queue / Verified) that were
 * three lenses on the same clusters. The lens is now the State COLUMN, and the ranking is one pure
 * function on the API (`rankWorklist`).
 *
 * 🚨 TWO THINGS DELIBERATELY ABSENT, both decided by the screen's owner:
 *
 * 1. THERE IS NO DATE FILTER. Every number here is all-time. A cluster wrong since August outranks
 *    one that appeared last week, which is the order worth fixing in — and the old dropdown governed
 *    the cluster counts while the verified figures ignored it, with nothing saying which half.
 *
 * 2. THERE IS NO "ENGINE AT TIME OF CHECK" ANYWHERE. Only today's configuration can be fixed, so
 *    only today's configuration is shown. The `QUOTED` picks still exist in the database and nothing
 *    in this UI reads them; they would come back as a per-check detail if support ever needs to
 *    answer "what were we quoting in August".
 *
 * 🚨 AND NO KEYBOARD SHORTCUTS. Selecting a check is done by clicking its row, and that is the only
 * mechanism — no digit keys, no J/K, no global keydown handler.
 */
export function BenefitComparisonTab() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const accountId = params.get(PARAM.account) ?? undefined;
  const payerId = params.get(PARAM.payer) ?? undefined;
  const benefit = oneOf(params.get(PARAM.benefit), COMPARISON_BENEFITS);
  /**
   * 🚨 ONE MECHANISM, NOT TWO. The Regressed card's "Show the 3" used to set its own `regressed=1`
   * flag; it now sets this filter to `REGRESSED`, so the card and the dropdown cannot disagree about
   * what the table is showing.
   */
  const state = oneOf(params.get(PARAM.state), [...WORKLIST_STATES]);
  const regressedOnly = state === "REGRESSED";

  const update = (changes: Record<string, string | undefined>, push = false) =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        Object.entries(changes).forEach(([key, value]) => {
          if (value === undefined) next.delete(key);
          else next.set(key, value);
        });
        return next;
      },
      { replace: !push }
    );

  /**
   * 🚨 BENEFIT IS NOT SENT. Account and payer change what the WHOLE PAGE reports and go to the API;
   * Benefit now filters the worklist table alone, in the browser, beside State. The coverage strip,
   * the Engine accuracy card, the agreement cards and the configuration gaps deliberately keep
   * showing every benefit while the table is narrowed to one.
   */
  const filters: IWorklistFilters = useMemo(
    () => ({ accountId, payerId }),
    [accountId, payerId]
  );
  const { data, isLoading, isError } = useGetBenefitWorklistQuery(filters);
  const worklist = data?.data;
  const verified = useGetBenefitVerifiedStatusQuery(filters).data?.data;

  const clusterAccount = params.get(PARAM.clusterAccount);
  const clusterBenefit = oneOf(
    params.get(PARAM.clusterBenefit),
    COMPARISON_BENEFITS
  );
  const clusterReadFrom = params.get(PARAM.clusterReadFrom);
  if (clusterAccount && clusterBenefit && clusterReadFrom) {
    const request: IComparisonChecksRequest = {
      accountId: clusterAccount,
      payerId: params.get(PARAM.clusterPayer) ?? "none",
      benefit: clusterBenefit,
      readFrom: clusterReadFrom,
      // 🚨 Today's configuration, always. There is no engine-side parameter on this screen.
      engine: "REPLAY",
      ...(params.get(PARAM.clusterNoPlan) === "1"
        ? { planNameMissing: true }
        : { planName: params.get(PARAM.clusterPlan) ?? undefined }),
    };
    return (
      <Surface data-testid="benefitComparisonTab">
        <BenefitComparisonCluster
          request={request}
          state={oneOf(
            params.get(PARAM.clusterState),
            Object.keys(WORKLIST_STATE_LABELS) as WorklistState[]
          )}
          onBack={() =>
            update(
              Object.fromEntries(CLUSTER_PARAMS.map((key) => [key, undefined])),
              true
            )
          }
        />
      </Surface>
    );
  }

  const openCluster = (row: IWorklistRow) => {
    // An unconfigured account has no cluster to open: the answer is to write a configuration.
    if (row.state === "NOT_CONFIGURED" || row.benefit === null) {
      navigate(`/hospitals/${row.accountId}?tab=benefits`);
      return;
    }
    update(
      {
        [PARAM.clusterAccount]: row.accountId,
        [PARAM.clusterPayer]:
          row.payerId === null ? "none" : String(row.payerId),
        [PARAM.clusterPlan]: row.planName ?? undefined,
        [PARAM.clusterNoPlan]: row.planName === null ? "1" : undefined,
        [PARAM.clusterBenefit]: row.benefit,
        [PARAM.clusterReadFrom]: row.readFrom,
        [PARAM.clusterState]: row.state,
      },
      true
    );
  };

  const actionBody = (row: IWorklistRow) => (
    <Button
      type="button"
      label={row.state === "NOT_CONFIGURED" ? "Configure" : "Analyze"}
      className="p-button-text p-button-sm"
      data-testid="benefitWorklist.open"
      onClick={() => openCluster(row)}
    />
  );

  const coverage = worklist?.coverage;
  const totals = verified?.totals;
  /**
   * The table's own two filters, both applied HERE rather than by the API.
   *
   * 🚨 IT KEEPS EVERYTHING ABOVE THE TABLE HONEST. The Regressed count, the coverage bars and the
   * agreement cards are all-time figures; narrowing the table must not move them, and filtering
   * server-side would.
   *
   * 🚨 A `NOT_CONFIGURED` ROW SURVIVES A BENEFIT FILTER, because its `benefit` is null meaning ALL of
   * them — the account has no configuration for the selected benefit either, so the row is as true
   * under the filter as without it. Dropping it would hide a real gap from the person narrowing down
   * to the benefit it affects.
   */
  const rows = (worklist?.rows ?? []).filter(
    (row) =>
      (!state || row.state === state) &&
      (!benefit || row.benefit === benefit || row.benefit === null)
  );

  return (
    <Surface data-testid="benefitComparisonTab">
      <Tooltip target={`.${TOOLTIP_CLASS}`} position="top" />
      {/*
       * 🚨 Account and payer. Nothing else up here — no date, no chips, no view switch.
       *
       * Benefit used to stand here too, and used to narrow the whole page. It moved down beside the
       * table and now filters only that table — so these two are exactly the filters that change what
       * the CARDS report, and the two below are exactly the ones that change which ROWS are listed.
       */}
      <div className="flex flex-wrap align-items-end gap-3 mb-4">
        <div className="flex flex-column gap-1">
          <label htmlFor="benefitWorklist.account">Account</label>
          <Dropdown
            inputId="benefitWorklist.account"
            value={accountId ?? null}
            options={worklist?.options.accounts ?? []}
            optionLabel="accountName"
            optionValue="accountId"
            placeholder="All accounts"
            showClear
            filter
            className="w-16rem"
            onChange={(e) => update({ [PARAM.account]: e.value ?? undefined })}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label htmlFor="benefitWorklist.payer">Payer</label>
          <Dropdown
            inputId="benefitWorklist.payer"
            value={payerId ?? null}
            options={(worklist?.options.payers ?? []).map((payer) => ({
              label: payer.payerName,
              value: String(payer.payerId),
            }))}
            placeholder="All payers"
            showClear
            filter
            className="w-14rem"
            onChange={(e) => update({ [PARAM.payer]: e.value ?? undefined })}
          />
        </div>
      </div>

      {isError && (
        <Message
          severity="error"
          className="mb-3"
          text="The engine worklist could not be loaded."
        />
      )}

      <div className="grid mb-2">
        <div className="col-12 md:col-6 xl:col-3">
          <Card
            label="Regressed"
            testId="benefitWorklist.regressed"
            tone={
              worklist && worklist.regressed.count > 0 ? "danger" : undefined
            }
          >
            <span
              className={`align-self-center text-4xl font-bold ${
                worklist?.regressed.count
                  ? "text-red-700"
                  : "text-color-secondary"
              }`}
            >
              {worklist ? worklist.regressed.count.toLocaleString() : "—"}
            </span>
            <Muted>
              <span className="block text-center">{REGRESSED_CARD_TEXT}</span>
              {worklist?.regressed.count ? (
                <>
                  {" "}
                  Broke under config {worklist.regressed.configVersion ?? "—"}
                  {worklist.regressed.at
                    ? `, ${relativeTime(worklist.regressed.at)}`
                    : ""}
                  .{" "}
                  <Button
                    type="button"
                    className="p-button-link p-0 font-semibold"
                    label={
                      regressedOnly
                        ? "Show everything"
                        : `Show the ${worklist.regressed.count} →`
                    }
                    data-testid="benefitWorklist.showRegressed"
                    onClick={() =>
                      update({
                        [PARAM.state]: regressedOnly ? undefined : "REGRESSED",
                      })
                    }
                  />
                </>
              ) : null}
            </Muted>
          </Card>
        </div>

        <div className="col-12 md:col-6 xl:col-3">
          <Card
            label="Engine accuracy"
            testId="benefitWorklist.accuracy"
            tooltip={ENGINE_ACCURACY_TOOLTIP}
          >
            <span
              className={`align-self-center text-4xl font-bold ${
                totals?.verified ? "" : "text-color-secondary"
              }`}
              data-testid="benefitWorklist.accuracy.headline"
            >
              {totals?.verified
                ? accuracyText(totals.matches, totals.verified)
                : "—%"}
            </span>
            {/*
             * 🚨 COLUMN FLOW, three rows: deductible, out-of-pocket and co-insurance go down the
             * left, copay and physician copay down the right — the order the mockups fixed, so the
             * same benefit is in the same place every time. Row flow puts them somewhere else.
             *
             * Each percentage is pushed to the RIGHT of its own cell so the five land on two
             * vertical lines rather than wherever their label happened to end; they are read down
             * the column against one another, and ragged numbers defeat that.
             */}
            <AccuracyGrid className="text-sm">
              {ACCURACY_BENEFIT_ORDER.map((each) => {
                const cell = verified?.benefits.find(
                  (row) => row.benefit === each
                );
                return (
                  <span
                    key={each}
                    className="flex align-items-baseline justify-content-between gap-2"
                    data-testid={`benefitWorklist.accuracy.${each}`}
                  >
                    <span>{SCORECARD_BENEFIT_LABELS[each]}</span>
                    <strong
                      className={`white-space-nowrap ${
                        cell?.verified ? "" : "text-color-secondary"
                      }`}
                    >
                      {cell?.verified
                        ? accuracyText(cell.matches, cell.verified)
                        : "—%"}
                    </strong>
                  </span>
                );
              })}
            </AccuracyGrid>
          </Card>
        </div>

        <div className="col-12 md:col-6 xl:col-3">
          <Card
            label="Since last config change"
            testId="benefitWorklist.sinceChange"
          >
            {worklist?.sinceLastConfigChange.changedAt ? (
              <>
                <div className="flex-1 flex align-items-center justify-content-center gap-5">
                  <span className="flex flex-column align-items-center">
                    <span className="text-4xl font-bold text-green-700">
                      +{worklist.sinceLastConfigChange.fixed}
                    </span>
                    <Muted>fixed</Muted>
                  </span>
                  <span className="flex flex-column align-items-center">
                    <span className="text-4xl font-bold text-red-700">
                      −{worklist.sinceLastConfigChange.regressed}
                    </span>
                    <Muted>regressed</Muted>
                  </span>
                </div>
                <Muted>
                  <span className="block text-center">
                    {relativeTime(worklist.sinceLastConfigChange.changedAt)}
                  </span>
                </Muted>
              </>
            ) : (
              <span className="flex-1 flex align-items-center justify-content-center text-4xl font-bold text-color-secondary">
                —
              </span>
            )}
          </Card>
        </div>

        <div className="col-12 md:col-6 xl:col-3">
          <Card label="Trend" testId="benefitWorklist.trend">
            <BenefitWorklistTrend />
          </Card>
        </div>
      </div>

      {/*
       * 🚨 NOT PrimeFlex `grid`/`col` in here. `.grid` carries a −0.5rem margin, so the inner row of
       * bars hung half a rem to the left of its own group heading while the other group (a plain
       * grid) sat flush — the two groups were indented differently on the same strip. Plain CSS grid
       * both times: one row of groups with a real gap between them, one grid of bars inside each,
       * every left edge on the same line.
       */}
      <div className="border-1 surface-border border-round p-3 mb-4">
        <div className="flex flex-wrap align-items-baseline gap-2 mb-3">
          <span className="text-xs font-bold uppercase text-color-secondary">
            Coverage
          </span>
          <Muted>{COVERAGE_INTRO(coverage?.checks ?? 0)}</Muted>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(26rem, 1fr))",
            gap: "1.5rem 4rem",
          }}
        >
          <div>
            <span className="font-bold block mb-3">
              Benefit selector algorithms
            </span>
            <div
              style={COVERAGE_ROWS}
              data-testid="benefitWorklist.coverage.selectors"
            >
              <CoverageBar
                label="Legacy"
                count={coverage?.legacy ?? 0}
                total={coverage?.checks ?? 0}
              />
              <CoverageBar
                label="Engine"
                count={coverage?.engine ?? 0}
                total={coverage?.checks ?? 0}
              />
              <CoverageBar
                label="AI"
                count={coverage?.ai ?? 0}
                total={coverage?.checks ?? 0}
              />
            </div>
          </div>
          <div>
            <span className="font-bold block mb-3">
              Sources of truth to compare them against
            </span>
            <div
              style={COVERAGE_ROWS}
              data-testid="benefitWorklist.coverage.truth"
            >
              <CoverageBar
                label="Remits"
                count={coverage?.remits ?? 0}
                total={coverage?.checks ?? 0}
                accent
              />
              <CoverageBar
                label="Human input"
                count={coverage?.verdicts ?? 0}
                total={coverage?.checks ?? 0}
                accent
              />
            </div>
          </div>
        </div>
      </div>

      {/*
       * CLE-2820 follow-up — HOW OFTEN THE THREE SELECTORS AGREE, one card per benefit.
       *
       * 🚨 THE ONLY NUMBER DEDUCTIBLE AND OUT-OF-POCKET HAVE. The Engine accuracy card above shows
       * "—%" for both and always will: a remit says what the patient owed on one claim, never what
       * they had REMAINING at check time, so neither benefit has a truth source and
       * `BenefitVerificationStatus` holds zero rows for them. Agreement needs no truth at all.
       *
       * 🚨 AND IT IS NOT ACCURACY. Three selectors agreeing on a wrong value agree just as loudly.
       * The caption says so, because a row of high percentages beside a card headed ENGINE ACCURACY
       * would otherwise read as a second accuracy score.
       */}
      <div className="border-1 surface-border border-round p-3 mb-4">
        <div className="flex flex-wrap align-items-baseline gap-2 mb-3">
          <span className="text-xs font-bold uppercase text-color-secondary">
            {AGREEMENT_HEADING}
          </span>
          <i
            className={`pi pi-info-circle text-color-secondary ${TOOLTIP_CLASS}`}
            data-pr-tooltip={AGREEMENT_TOOLTIP}
            data-testid="benefitWorklist.agreement.tooltip"
          />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(17rem, 1fr))",
            gap: "1rem",
          }}
          data-testid="benefitWorklist.agreement"
        >
          {SCORECARD_BENEFIT_ORDER.map((benefit) => {
            const cell = worklist?.agreement.find(
              (each) => each.benefit === benefit
            );
            return (
              <AgreementCard
                key={benefit}
                cell={
                  cell ?? {
                    benefit,
                    compared: 0,
                    allThree: 0,
                    legacyAndEngine: 0,
                    legacyAndAi: 0,
                    engineAndAi: 0,
                    none: 0,
                  }
                }
              />
            );
          })}
        </div>
      </div>

      {/*
       * 🚨 No "engine last run …" here. It was never in the design and it answered a question
       * nobody asks of a worklist; the cluster screen states it beside the re-run button, which is
       * the one place it changes what someone does next.
       */}
      <div className="flex flex-wrap align-items-baseline gap-3 mb-3">
        <h3 className="m-0">Worklist</h3>
        <Muted>{WORKLIST_INTRO}</Muted>
      </div>

      {/*
       * 🚨 THE TWO FILTERS THAT ANSWER "WHICH ROWS OF THIS TABLE", next to the table.
       *
       * Both narrow THIS TABLE AND NOTHING ELSE, in the browser. Benefit used to be a page filter and
       * went to the API; it no longer does, so the coverage strip, the accuracy card and the agreement
       * cards go on reporting every benefit while the table shows one. That is deliberate — those are
       * all-time figures and a table filter must not move them.
       */}
      <div className="flex flex-wrap align-items-end gap-3 mb-3">
        <div className="flex flex-column gap-1">
          <label htmlFor="benefitWorklist.state">State</label>
          <Dropdown
            inputId="benefitWorklist.state"
            value={state ?? null}
            options={WORKLIST_STATES.map((each) => ({
              label: WORKLIST_STATE_LABELS[each],
              value: each,
            }))}
            placeholder="All states"
            showClear
            /*
             * 🚨 SIZED FOR THE LONGEST LABEL, which is "Human input disagrees" — with
             * "Legacy + AI disagree" and "Every source differs" a character behind it. At the 14rem
             * the other filters use, the clear and chevron icons leave under 150px for text and the
             * selected state reads "Legacy + AI dis…", which is the one thing this control exists to
             * tell you. It is on its own row, so the extra width costs the other filters nothing.
             */
            className="w-20rem"
            onChange={(e) => update({ [PARAM.state]: e.value ?? undefined })}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label htmlFor="benefitWorklist.benefit">Benefit</label>
          <Dropdown
            inputId="benefitWorklist.benefit"
            value={benefit ?? null}
            options={COMPARISON_BENEFITS.map((each) => ({
              label: COMPARISON_BENEFIT_LABELS[each],
              value: each,
            }))}
            placeholder="All benefits"
            showClear
            className="w-14rem"
            onChange={(e) => update({ [PARAM.benefit]: e.value ?? undefined })}
          />
        </div>
      </div>

      {/*
       * 🚨 PAGINATED AT 50, client side: the API ranks and returns the whole list, and this is one
       * ranked queue meant to be worked from the top — sorting or filtering a page of it would
       * answer a different question. The API's own cap is `WORKLIST_LIMIT`.
       */}
      <DataTable
        value={rows}
        paginator
        rows={WORKLIST_PAGE_SIZE}
        loading={isLoading}
        rowClassName={(row: IWorklistRow) =>
          row.state === "REGRESSED" ? "bg-red-50" : ""
        }
        emptyMessage="Nothing to fix: today’s engine agrees with every remit, verdict, legacy and AI value in this filter."
        data-testid="benefitWorklist.rows"
      >
        <Column
          header="#"
          body={(_row, options) => options.rowIndex + 1}
          style={{ width: "3rem" }}
        />
        {/*
         * 🚨 EVERY SHORT COLUMN HAS A FIXED WIDTH AND THE ACCOUNT COLUMN HAS NONE, so the slack on a
         * wide screen goes to the one column that can use it. Sized on content it took nearly half
         * the table and the short ones wrapped for want of a few pixels; pinned to 26rem it stayed
         * squeezed while "Human verdicts" sat on inches of white space for the word "—". The plan
         * name is the only thing here allowed to wrap, being the only thing long enough to need to.
         */}
        <Column
          header="Account · Payer · Plan"
          body={nameBody}
          style={{ minWidth: "18rem" }}
        />
        <Column
          header="Benefit"
          style={{ width: "9rem" }}
          body={(row: IWorklistRow) =>
            row.benefit ? COMPARISON_BENEFIT_LABELS[row.benefit] : "All"
          }
          headerClassName="white-space-nowrap"
          bodyClassName="white-space-nowrap"
        />
        <Column
          header="State"
          style={{ width: "11rem" }}
          body={stateBody}
          headerClassName="white-space-nowrap"
        />
        {/* 🚨 The column that makes the rank order legible. Do not drop it. */}
        <Column
          header="Checks"
          style={{ width: "6rem" }}
          body={(row: IWorklistRow) =>
            row.total === 0
              ? row.affected.toLocaleString()
              : `${row.affected} / ${row.total}`
          }
          headerClassName="white-space-nowrap"
          bodyClassName="white-space-nowrap"
        />
        <Column
          header="Engine says"
          style={{ width: "8rem" }}
          body={engineSaysBody}
          headerClassName="white-space-nowrap"
          bodyClassName="white-space-nowrap"
        />
        <Column
          header="Other sources say"
          style={{ width: "13rem" }}
          body={otherSourcesBody}
          headerClassName="white-space-nowrap"
        />
        <Column
          header="Engine read from"
          style={{ width: "11rem" }}
          body={readFromBody}
          headerClassName="white-space-nowrap"
        />
        <Column
          header="Human input"
          style={{ width: "8rem" }}
          body={(row: IWorklistRow) => verdictsText(row)}
          headerClassName="white-space-nowrap"
          bodyClassName="white-space-nowrap"
        />
        <Column header="" body={actionBody} />
      </DataTable>
    </Surface>
  );
}
